import type { Subscription } from "~~/types/subscription";
import type { User, UserOrganization } from "~~/types/user";
import defaultAvatar from "@/assets/images/avatar-placeholder.png";

export const useUserStore = defineStore(
  "user-store",
  () => {
    //state
    const authToken = ref<string | null>();
    const refreshToken = ref<string | null>();

    const user = ref<User | null>(null);
    const organizations = ref<UserOrganization[]>([]);

    //helpers
    const api = apiRepository();
    const organizationStore = useOrganizationStore();
    const subscriptionStore = useSubscriptionStore();
    const { imagesURL } = useRuntimeConfig().public;

    //actions
    function setUser(newUser: User) {
      user.value = newUser;
    }
    function setUserImage(newImage: string) {
      if (user.value) {
        user.value.profileImage = newImage;
      }
    }
    function setOrganizations(newOrganizations: UserOrganization[]) {
      organizations.value = newOrganizations;
    }

    function setTokens(
      newAuthToken: string,
      newRefreshToken: string,
    ) {
      authToken.value = newAuthToken;
      refreshToken.value = newRefreshToken;
    }

    async function fetchUserData(
      organization: string | undefined = undefined,
    ) {
      const endPoint = organization
        ? `/auth/me?organizationUrl=${organization}`
        : "/auth/me";
      const { success, data } = await api.get<{
        user: User;
        currentOrganization: UserOrganization;
        organizations: UserOrganization[];
        subscriptionDetails: Subscription;
      }>(endPoint, {
        requiresAuthorization: true,
      });
      if (success && data) {
        setUser(data.user);
        setOrganizations(data.organizations);
        organizationStore.setOrganization(data.currentOrganization);
        subscriptionStore.setSubscription(data.subscriptionDetails);
      }
    }

    function fetchCurrentOrgUser() {
      return fetchUserData(organizationStore.organizationUrl ?? "");
    }

    async function refreshTokens() {
      const { success, data } = await api.get<{
        token: string;
        refreshToken: string;
      }>("/auth/refresh-token", {
        requiresAuthorization: true,
        authToken: refreshToken.value ?? "",
      });
      if (success && data) {
        setTokens(data.token, data.refreshToken);
      }
      return success;
    }

    function completeProfile({
      firstName,
      lastName,
    }: {
      firstName: string;
      lastName: string;
    }) {
      if (user.value) {
        user.value.firstName = firstName;
        user.value.lastName = lastName;
        user.value.isProfileCompleted = true;
      }
    }

    function logOut() {
      authToken.value = null;
      user.value = null;

      organizationStore.reset();
      subscriptionStore.reset();
    }

    //getters
    const isLoggedIn = computed(() =>
      authToken.value ? true : false,
    );

    const id = computed(() => {
      return user.value?.id;
    });

    const image = computed(() => {
      if (user.value?.profileImage) {
        return `${imagesURL}/images/${user.value.profileImage}`;
      }
      return defaultAvatar;
    });

    const firstName = computed({
      get: () => (user.value ? user.value.firstName : ""),
      set: (value) => {
        if (user.value) {
          user.value.firstName = value;
        }
      },
    });
    const lastName = computed({
      get: () => (user.value ? user.value.lastName : ""),
      set: (value) => {
        if (user.value) {
          user.value.lastName = value;
        }
      },
    });

    const email = computed(() => {
      return user.value ? user.value.email : "";
    });

    const isProfileCompleted = computed(() =>
      user.value ? user.value.isProfileCompleted : !isLoggedIn.value,
    );

    return {
      user,
      authToken,
      refreshToken,
      isLoggedIn,
      image,
      isProfileCompleted,
      organizations,
      firstName,
      lastName,
      email,
      id,

      fetchUserData,
      fetchCurrentOrgUser,
      refreshTokens,
      logOut,
      completeProfile,
      setUser,
      setUserImage,
      setOrganizations,
      setAuthToken: setTokens,
    };
  },
  {
    persist: {
      pick: ["authToken", "refreshToken"],
    },
  },
);
